<template>
  <b-modal
    id="apply-game-modal"
    hide-footer
    size="sm"
    centered
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <!-- Title -->
    <template #modal-title>
      <div class="d-flex align-items-center">
        <span class="font-weight-bold">Apply Game</span>
        <b-icon-gift class="ml-1 text-primary" />
      </div>
    </template>

    <!-- Subtitle -->
    <div class="text-center mb-1">
      <h5 class="font-weight-bold mb-0 text-black">Select Game</h5>
      <small class="text-muted">
        Choose from available game templates to apply.
      </small>
    </div>

    <!-- Select -->
    <b-form-group label="Game" label-class="text-black">
      <v-select
        v-model="selectedGame"
        :options="games"
        :filterable="false"
        label="name"
        placeholder="Search game..."
        @search="onSearch"
        @open="onOpen"
      >
        <template #option="{ name }">
          <div>{{ name }}</div>
        </template>

        <template #selected-option="{ name }">
          <div>{{ name }}</div>
        </template>

        <template #list-footer>
          <div
            v-if="hasMore"
            ref="loadMoreTrigger"
            class="text-center pb-1 text-muted"
          >
            <b-spinner small />
          </div>
        </template>
      </v-select>
    </b-form-group>

    <!-- Description -->
    <b-form-group label="Description" class="text-black">
      <b-form-textarea
        v-model="description"
        disabled
        rows="3"
        placeholder="Description will appear after game selected"
      />
    </b-form-group>

    <!-- Buttons -->
    <b-button
      block
      variant="success"
      class="mb-1 mt-2"
      :disabled="loadingCreatePromotion || !selectedGame"
      @click="handleApplyGame"
    >
      APPLY GAME
    </b-button>

    <b-button block variant="outline-secondary" @click="hideModal">
      CANCEL
    </b-button>
  </b-modal>
</template>
<script>
import useToastHandler from "@/services/toastHandler";
import {
  BButton,
  BModal,
  BFormGroup,
  BFormTextarea,
  BIconGift,
  BSpinner,
} from "bootstrap-vue";
import { mapActions, mapState } from "vuex";
import vSelect from "vue-select";

export default {
  components: {
    BButton,
    BModal,
    BFormGroup,
    BFormTextarea,
    BIconGift,
    vSelect,
    BSpinner,
  },
  data() {
    return {
      selectedGame: null,
      description: "",
      games: [],
      page: 1,
      limit: 20,
      keyword: "",
      hasMore: true,
      loadingList: false,
      observer: null,
    };
  },
  computed: {
    ...mapState("promotions/list", ["loadingCreatePromotion"]),
  },
  watch: {
    selectedGame(val) {
      this.description = val?.description || "";
    },
  },
  methods: {
    ...mapActions({
      getPromotionsList: "promotions/list/getPromotionsList",
      updatePromotionTargets: "promotions/list/updatePromotionTargets",
      setReloadListDepositMatches:
        "promotions/list/setReloadListDepositMatches",
      fetchUser: "user/detail/asyncUser",
    }),

    async fetchGames(reset = false) {
      if (this.loadingList || (!this.hasMore && !reset)) return;
      this.loadingList = true;
      const res = await this.getPromotionsList({
        page: this.page,
        limit: this.limit,
        type: "Game",
        keyword: this.keyword,
      });
      const data = res?.data || [];
      if (reset) {
        this.games = data;
      } else {
        this.games.push(...data);
      }
      this.hasMore = this.games.length < (res?.pagination?.total || 0);
      this.page++;
      this.loadingList = false;
      this.$nextTick(() => {
        if (this.hasMore) {
          this.initObserver();
        }
      });
    },
    onSearch(search) {
      this.keyword = search;
      this.page = 1;
      this.hasMore = true;
      this.disconnectObserver();
      this.fetchGames(true);
    },
    onOpen() {
      if (!this.games.length) this.fetchGames(true);
      this.$nextTick(this.initObserver);
    },
    initObserver() {
      this.$nextTick(() => {
        const dropdown = document.querySelector(".vs__dropdown-menu");
        const trigger = this.$refs.loadMoreTrigger;
        if (!dropdown || !trigger) return;
        this.disconnectObserver();
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              this.fetchGames();
            }
          },
          {
            root: dropdown,
            rootMargin: "50px",
          }
        );
        this.observer.observe(trigger);
      });
    },
    disconnectObserver() {
      this.observer?.disconnect();
      this.observer = null;
    },
    onModalShown() {
      this.reset();
    },
    onModalHidden() {
      this.reset();
      this.disconnectObserver();
    },
    reset() {
      this.selectedGame = null;
      this.description = "";
      this.games = [];
      this.page = 1;
      this.keyword = "";
      this.hasMore = true;
    },
    async handleApplyGame() {
      const res = await this.updatePromotionTargets({
        promotionId: this.selectedGame.id,
        targets: [
          {
            targetType: "User",
            targetId: this.$route.params.id,
          },
        ],
      });
      if (res) {
        this.showToastSuccess("success", "Applied successfully!");
        this.hideModal();
        this.setReloadListDepositMatches(true);
        this.fetchUser({ id: this.$route.params.id });
      }
    },
    hideModal() {
      this.$bvModal.hide("apply-game-modal");
    },
  },
  setup() {
    const { showToastSuccess, showToastError } = useToastHandler();
    return { showToastSuccess, showToastError };
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
